import { ReactUtils } from '@festina-life/core'
import cx from 'classnames'
import { type JSX, memo, type MouseEvent } from 'react'

import { NavLink } from '../../actions/navLink/NavLink'
import { Td, Tr } from '../table/Table'
import type { RowAction } from '../types'
import type { Column, ColumnId } from './createColumnDefiner'
import { SortableTableDataCell } from './SortableTableDataCell'
import { SortableTableRowActionButton } from './SortableTableRowActionButton'

type Props<T> = {
  row: T
  columns: Record<ColumnId, Column<T>>
  rowOnClick?: (row: T, event: MouseEvent<HTMLTableRowElement>) => void
  rowActions?: (row: T) => RowAction[]
  to: string | undefined
  isSelected: boolean | undefined
  className: JSX.IntrinsicElements['tr']['className']
  rightStickyCellStyles: string
}

export const SortableTableRowInternal = <T,>({
  row,
  columns,
  rowOnClick,
  rowActions,
  to,
  isSelected,
  className,
  rightStickyCellStyles,
}: Props<T>) => (
  <ReactUtils.ConditionalWrap
    condition={!!to}
    wrap={(children) => (
      <NavLink className="contents! before:hidden!" to={to!}>
        {children}
      </NavLink>
    )}
  >
    <Tr
      {...(rowOnClick && {
        role: 'button',
        onClick: (event) => rowOnClick(row, event),
      })}
      isRowClickable={!!rowOnClick || typeof to === 'string'}
      isRowSelected={isSelected}
      className={className}
    >
      {Object.entries(columns).map(([columnId, column]) => (
        <SortableTableDataCell key={columnId} column={column} row={row} />
      ))}
      {rowActions && (
        <Td className={cx('w-0 whitespace-nowrap p-0!', rightStickyCellStyles)}>
          {rowActions(row).map(({ key, ...rowAction }) => (
            <SortableTableRowActionButton key={key} rowAction={rowAction} />
          ))}
        </Td>
      )}
    </Tr>
  </ReactUtils.ConditionalWrap>
)

// Crucial to memoize the row component to prevent unnecessary re-renders
export const SortableTableRow = memo(
  SortableTableRowInternal,
) as typeof SortableTableRowInternal
